import { SEAT_LIMIT_ACCEPT_CAUSE } from '@bower/shared/billing/gates';

/** What an accept refusal means for the surface showing it. */
export interface AcceptRefusalPresentation {
  /** Keep the pending token: the refusal is retryable once the workspace
   *  changes (a seat is added), so the invite link must stay live. */
  readonly keepToken: boolean;
  readonly toast: {
    readonly title: string;
    readonly description: string;
    readonly status: 'error';
    readonly duration: number;
    readonly isClosable: true;
  };
}

/**
 * Decide the copy + token handling for a refused invitation accept.
 *
 * A MEMBER_LIMIT refusal is not transient — retrying without a seat being
 * added cannot work (19 futile retries on 2026-08-27) — but it IS retryable
 * the moment the workspace adds one, so it keeps the pending token and names
 * who can fix it. Every other refusal clears the token as before and gets a
 * generic explanation, which is still strictly more than the silent dashboard
 * landing this replaced (Dashboard never reads the navigation state).
 */
export function describeAcceptRefusal(result: {
  readonly error?: string;
  readonly errorCause?: string | null;
}): AcceptRefusalPresentation {
  if (result.errorCause === SEAT_LIMIT_ACCEPT_CAUSE) {
    return {
      keepToken: true,
      toast: {
        title: "Your team's workspace is out of seats",
        description:
          'Your invitation is still valid. Ask the person who invited you to add a seat, then open your invite link again.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      },
    };
  }
  return {
    keepToken: false,
    toast: {
      title: "We couldn't accept your invitation",
      description: result.error || 'Failed to accept invitation',
      status: 'error',
      duration: 9000,
      isClosable: true,
    },
  };
}
